import { Marker, Popup, useMap } from "@vis.gl/react-maplibre";
import { FC, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  getAliveMap,
  MAP_VEHICLE_TRACKING_POPUP_CLASS,
  MAP_VEHICLE_TRACKING_POPUP_MAX_WIDTH_PX,
  MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT,
  MapTooltip,
} from "@ryde/controls";
import { TLatLngTuple } from "@ryde/shared";

import {
  TRACKING_POPUP_EASE_DURATION_MS,
  TRACKING_POPUP_OFFSET_PX,
  TRACKING_POPUP_VERTICAL_OFFSET_PX,
} from "../route-block-maplibre.constants";
import {
  ICourseCodePin,
  TCarTrackingInfo,
} from "./maplibre-tracking-route.models";
import { MapLibreVehicleCarMarker } from "./maplibre-vehicle-car-marker";

/**
 * The vehicle at its latest GPS fix, plus the tracking popup it opens. Rides configured with a client course
 * code show that code's pin (markup shared with Leaflet, hence `dangerouslySetInnerHTML`); every other ride
 * shows the car glyph.
 */
export const MapLibreTrackingVehicleMarker: FC<{
  position: TLatLngTuple;
  carTrackingInfo: TCarTrackingInfo;
  colorValue: string;
  inDetour: boolean;
  coursePin: ICourseCodePin | null;
  courseId?: number;
  passengersCount?: number;
  debugMode: boolean;
  onOpenRide?: () => void;
}> = ({
  position,
  carTrackingInfo,
  colorValue,
  inDetour,
  coursePin,
  courseId,
  passengersCount,
  debugMode,
  onOpenRide,
}) => {
  const { current: mapRef } = useMap();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [lat, lng] = position;

  const handleMarkerClick = useEvent((e: { originalEvent: MouseEvent }) => {
    e.originalEvent.stopPropagation();
    setIsPopupOpen(true);

    // MapLibre has no Leaflet-style autoPan — nudge so the popup stays in frame.
    getAliveMap(mapRef)?.easeTo({
      center: [lng, lat],
      offset: [0, TRACKING_POPUP_VERTICAL_OFFSET_PX],
      duration: TRACKING_POPUP_EASE_DURATION_MS,
    });
  });
  const handlePopupClose = useEvent(() => setIsPopupOpen(false));

  // After hooks, same as the station pin: a GPS poll can send a non-finite fix, and mounting Marker
  // with that lng/lat is the same crash as an unfitted ride-card camera.
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return (
    <>
      <Marker
        longitude={lng}
        latitude={lat}
        anchor={coursePin ? "bottom" : "center"}
        {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
        onClick={handleMarkerClick}
      >
        <div
          className={
            coursePin
              ? "maplibre-vehicle-marker"
              : "maplibre-vehicle-marker maplibre-vehicle-marker--car"
          }
        >
          {coursePin ? (
            <div
              className={coursePin.className}
              dangerouslySetInnerHTML={{ __html: coursePin.html }}
            />
          ) : (
            <MapLibreVehicleCarMarker
              fillColor={colorValue}
              isDetour={inDetour}
            />
          )}
        </div>
      </Marker>

      {isPopupOpen && (
        <Popup
          longitude={lng}
          latitude={lat}
          anchor="bottom"
          offset={TRACKING_POPUP_OFFSET_PX}
          onClose={handlePopupClose}
          className={MAP_VEHICLE_TRACKING_POPUP_CLASS}
          maxWidth={`${MAP_VEHICLE_TRACKING_POPUP_MAX_WIDTH_PX}px`}
          closeOnClick={false}
        >
          <MapTooltip
            {...carTrackingInfo}
            courseId={carTrackingInfo.courseId ?? courseId}
            passengersCount={passengersCount}
            debugMode={debugMode}
            onOpenRide={onOpenRide}
          />
        </Popup>
      )}
    </>
  );
};
